import { lazy, Suspense, type FC } from "react";
import { InputGroup, Form } from "react-bootstrap";
import Select, { components } from "react-select";
import type {
  ControlProps,
  IndicatorsContainerProps,
  OptionProps,
  SingleValue,
  ValueContainerProps,
  InputProps,
} from "react-select";

import phoneCodes from "./phoneCodes.json";
import { Trans, useTranslation } from "react-i18next";

// AIDEV-NOTE: react-world-flags je předsestavený webpack bundle (256 data-URI SVG),
// tree-shaking na něj nefunguje a ve vstupním chunku váží 1,1 MB po gzipu.
// PhoneInput žije jen v Nastavení/Onboardingu, takže vlajku načítáme líně
// a definujeme na úrovni modulu, aby si všechny instance (i 242× v otevřené
// nabídce předvoleb) sdílely jeden lazy komponent a tedy jeden fetch chunku.
const ReactWorldFlags = lazy(async () => await import("react-world-flags"));

// Stejná šířka jako vlajka (width="24px"), aby se rozvržení při načítání chunku neposunulo.
const FlagPlaceholder: FC = () => (
  <span className="phone-input__flag" style={{ display: "inline-block", width: "24px" }} />
);

interface ICode {
  name: string;
  value: string;
  label: string;
  code: string;
}

interface Props {
  onPhoneChange: (phone: string) => void;
  label: string;
  phoneNumber: string;
}

const parsePhoneNumber = (phoneNumber: string) => {
  let code = "+420";
  let phone = phoneNumber;

  if (phoneNumber.trim().startsWith("+")) {
    const phoneParts = phoneNumber.split(" ");

    if (phoneParts.length === 2) {
      code = phoneParts[0];
      phone = phoneParts[1];
    } else {
      for (const item of phoneCodes) {
        if (phoneNumber.startsWith(item.label)) {
          code = item.label;
          phone = phoneNumber.split(code)[1];
          break;
        }
      }
    }
  }
  return { code, phone };
};

const PhoneInput: FC<Props> = ({ label, onPhoneChange, phoneNumber }) => {
  const { code, phone } = parsePhoneNumber(phoneNumber);

  const { t } = useTranslation(["common"]);

  const handlePhoneChange = (newPhone: string) => {
    onPhoneChange(`${code} ${newPhone}`);
  };

  const handleCodeChange = (newVal: SingleValue<ICode>) => {
    const code = newVal?.value;

    if (typeof code === "string") {
      onPhoneChange(`${code.replace(/[^+0-9]/g, "")} ${phone}`);
    }
  };

  const CustomOption = ({ children, ...props }: OptionProps<ICode>) => (
    <components.Option {...props}>
      <Suspense fallback={<FlagPlaceholder />}>
        <ReactWorldFlags code={props.data.code} width="24px" className="phone-input__flag" />
      </Suspense>{" "}
      {children}
    </components.Option>
  );

  const CustomControl = ({ children, ...props }: ControlProps<ICode>) => {
    const value = props.getValue();

    if (value?.length !== 1) {
      return (
        <components.Control {...props} className="phone-select__value">
          {children}
        </components.Control>
      );
    }

    const code = value[0].value;
    const country = phoneCodes.find(item => item.value === code);

    if (country === undefined) return null;

    return (
      <components.Control {...props} className="phone-select__value">
        <Suspense fallback={<FlagPlaceholder />}>
          <ReactWorldFlags code={country.code} width="24px" className="phone-input__flag" />
        </Suspense>{" "}
        {children}
      </components.Control>
    );
  };

  const CustomIndicator = ({ children, ...props }: IndicatorsContainerProps<ICode>) => (
    <components.IndicatorsContainer {...props} className="phone-select__indicator">
      {children}
    </components.IndicatorsContainer>
  );

  const CustomValueContainer = ({ children, ...props }: ValueContainerProps<ICode>) => (
    <components.ValueContainer {...props} className="phone-select__value-container">
      {children}
    </components.ValueContainer>
  );

  const CustomInput = ({ children, ...props }: InputProps<ICode>) => (
    <components.Input {...props} className="phone-select__input">
      {children}
    </components.Input>
  );

  return (
    <div className="mb-32px">
      <Form.Label>{label}</Form.Label>
      <InputGroup className="phone-input">
        <Select
          className="phone-select"
          options={phoneCodes}
          value={phoneCodes.find(phoneCode => phoneCode.value === code)}
          onChange={handleCodeChange}
          isMulti={false}
          classNamePrefix={"phone-select"}
          isSearchable={false}
          components={{
            Option: CustomOption,
            Control: CustomControl,
            IndicatorSeparator: null,
            IndicatorsContainer: CustomIndicator,
            ValueContainer: CustomValueContainer,
            Input: CustomInput,
          }}
          noOptionsMessage={() => t("phoneInputPrefixNotFound")}
        />
        <Form.Control
          type="tel"
          name="phone"
          placeholder={t("phoneInputPlaceholder")}
          pattern="[0-9]{9}"
          maxLength={13}
          value={phone ?? ""}
          onChange={e => {
            handlePhoneChange(e.target.value.replace(/[^0-9]/g, ""));
          }}
          className="phone-input__phone"
        />
      </InputGroup>
      <p className="phone-consent className mb-0 mt-1">
        <Trans
          i18nKey="phoneConsent"
          components={{
            lnk: (
              <a
                href="https://www.to-das.cz/wp-content/uploads/2022/10/info_ZOU_SSGH.pdf"
                target="_blank"
                className="phone-consent__link"
                rel="noopener noreferrer"
              />
            ),
          }}
        />
      </p>
    </div>
  );
};

export default PhoneInput;
